
import { msg } from 'api';

import { clientCookies } from 'utils/cookies';

import { isUserLoggedIn } from 'apps/user/helpers';
import { setErrorMessage, setMessage } from 'apps/notifications/actions/notifications';

import Locations from 'apps/basket/models/Locations';
import ProductWarnings from 'apps/products/models/ProductWarnings';

const LOB_COOKIE_NAME = 'LOB_NTF';

const AdditionalWarnings = Object.freeze({
  PROMOTIONS_EXCEEDING_LIMIT: 'PROMOTIONS_EXCEEDING_LIMIT',
  LOGGED_OUT_BASKET: 'LOGGED_OUT_BASKET',
});

// Depending on location, some flash messages shall be displays and some shall not.
const getListOfWarningsForLocation = (location) => {
  if (!(location in Locations)) {
    // eslint-disable-next-line no-console
    console.error(`[getListOfWarningsForLocation] Unknown location: ${location}`);
  }

  switch (location) {
    case Locations.MINI_TROLLEY:
      return [
        ProductWarnings.TOTAL_QUANTITY_EXCEEDED,
        ProductWarnings.QUANTITY_PER_ITEM_EXCEEDED,
        AdditionalWarnings.PROMOTIONS_EXCEEDING_LIMIT,
        AdditionalWarnings.LOGGED_OUT_BASKET,
      ];
    case Locations.FOPLIST:
    case Locations.TROLLEY_SIDEBAR:
    default:
      return [
        ProductWarnings.TOTAL_QUANTITY_EXCEEDED,
        // ProductWarnings.QUANTITY_PER_ITEM_EXCEEDED displayed within FOP
        AdditionalWarnings.PROMOTIONS_EXCEEDING_LIMIT,
        AdditionalWarnings.LOGGED_OUT_BASKET,
      ];
  }
};

const displayGlobalNotifications = (
  dispatch,
  {
    betaFeatures,
    isLoggedIn,
    promotionsExceedingLimit,
    warningsAllowedToDisplay,
  }
) => {
  const showMsg = content => dispatch(setMessage(content));

  warningsAllowedToDisplay.forEach(warningAllowedToDisplay => {
    switch (warningAllowedToDisplay) {
      case AdditionalWarnings.LOGGED_OUT_BASKET: {
        if (isLoggedIn) {
          return;
        }

        const hasSeenLOBNotification = clientCookies.get(LOB_COOKIE_NAME);

        if (hasSeenLOBNotification) {
          return;
        }

        clientCookies.set(LOB_COOKIE_NAME, true, {
          secure: true,
        });

        if (betaFeatures.includes('LOS_PROMPT_LOGIN_ONLY')) {
          showMsg('Existing customer? <a href="/login">Log in</a>.');
        } else {
          // eslint-disable-next-line max-len
          showMsg(`New to ${msg.retailer}? <a href="/register">Register</a>.<br />Existing customer? <a href="/login">Log in</a>.`);
        }
        break;
      }

      case AdditionalWarnings.PROMOTIONS_EXCEEDING_LIMIT: {
        promotionsExceedingLimit.forEach(promotionExceedingLimit => {
          showMsg(`You've added the maximum number of items for this offer (${
            promotionExceedingLimit.ceiling
          }). Further items will be charged at their regular price.`);
        });
        break;
      }

      default:
    }
  });
};

const displayProductWarningsForSku = (
  dispatch,
  productWarningsForSku,
  {
    isLoggedIn,
    warningsAllowedToDisplay,
  }
) => {
  const showErrorMsg = content => dispatch(setErrorMessage(content));

  productWarningsForSku.forEach(productWarning => {
    if (!warningsAllowedToDisplay.includes(productWarning)) {
      return;
    }

    switch (productWarning) {
      case ProductWarnings.TOTAL_QUANTITY_EXCEEDED: {
        if (isLoggedIn) {
          return;
        }

        showErrorMsg(`You can't add any more items to your trolley.<br />
          <a href="/login">Log in</a> or <a href="/register">register</a> to continue.`);
        break;
      }

      case ProductWarnings.QUANTITY_PER_ITEM_EXCEEDED: {
        showErrorMsg('Max. quantity exceeded');
        break;
      }

      default:
    }
  });
};

/**
 * Gets warnings configuration for given location (which is a place the user changed quantity from) and calls
 * displayGlobalNotifications & displayProductWarningsForSku for displaying all necessary warnings.
 */
const checkProductWarnings = (dispatch, getState, location) => {
  const state = getState();
  const isLoggedIn = isUserLoggedIn(state.user);
  const { betaFeatures = [] } = state.user;
  const { promotionsExceedingLimit = [], productWarnings = {} } = state.basket;

  const warningsAllowedToDisplay = getListOfWarningsForLocation(location);

  const params = {
    betaFeatures,
    isLoggedIn,
    promotionsExceedingLimit,
    warningsAllowedToDisplay,
  };

  displayGlobalNotifications(dispatch, params);

  for (const sku in productWarnings) {
    if (Object.hasOwnProperty.call(productWarnings, sku)) {
      displayProductWarningsForSku(dispatch, productWarnings[sku], params);
    }
  }

  return Object.keys(productWarnings).length !== 0 ? productWarnings : null;
};

export default checkProductWarnings;
